import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ProgressBar from '../ProgressBar/ProgressBar';
import { isTextLesson, isVideoLockedForUser } from '../../utils/lessonUtils';
import './Sidebar.css';

export default function Sidebar({
  curriculum,
  courseId,
  currentLessonId,
  completedLessonIds = [],
  progressPercent = 0,
  coursePrice,
  hasFullAccess = true,
}) {
  const [openSections, setOpenSections] = useState(() =>
    curriculum?.map((_, i) => i === 0) ?? []
  );
  const completedSet = new Set((completedLessonIds || []).map((id) => String(id)));

  const toggleSection = (index) => {
    setOpenSections((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  if (!curriculum?.length) return null;

  return (
    <aside className="course-sidebar">
      <div className="course-sidebar-header">
        <h3 className="course-sidebar-title">Course content</h3>
        <span className="course-sidebar-count">
          {curriculum.reduce((acc, s) => acc + s.lessons.length, 0)} lessons
        </span>
        <div className="course-sidebar-progress">
          <ProgressBar value={progressPercent} height={6} showLabel />
        </div>
      </div>
      <nav className="course-sidebar-nav">
        {curriculum.map((section, sectionIndex) => (
          <div key={section.id} className="course-sidebar-section">
            <button
              type="button"
              className="course-sidebar-section-title"
              onClick={() => toggleSection(sectionIndex)}
              aria-expanded={openSections[sectionIndex]}
            >
              <span>{section.title}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={openSections[sectionIndex] ? 'course-sidebar-chevron open' : 'course-sidebar-chevron'}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {openSections[sectionIndex] && (
              <ul className="course-sidebar-lessons">
                {section.lessons.map((lesson, lessonIndex) => {
                  const locked = isVideoLockedForUser(lesson, coursePrice, hasFullAccess);
                  const href = isTextLesson(lesson)
                    ? `/course/${courseId}/read/${lesson.id}`
                    : `/course/${courseId}/learn/${lesson.id}`;
                  return (
                    <li key={`${sectionIndex}-${lessonIndex}-${lesson.id}`}>
                      <Link
                        to={href}
                        className={`course-sidebar-lesson ${currentLessonId === lesson.id ? 'active' : ''} ${locked ? 'course-sidebar-lesson-locked' : ''}`}
                      >
                        <span className="course-sidebar-lesson-icon">
                          {locked ? (
                            <span className="course-sidebar-lesson-lock" title="Video locked until purchase">🔒</span>
                          ) : (lesson.completed || completedSet.has(String(lesson.id))) ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          ) : (
                            <span className="course-sidebar-lesson-dot" />
                          )}
                        </span>
                        <span className="course-sidebar-lesson-text">{lesson.title}</span>
                        <span className="course-sidebar-lesson-meta">
                          {lesson.type === 'text' && (
                            <span className="course-sidebar-lesson-type">Text</span>
                          )}
                          <span className="course-sidebar-lesson-duration">{lesson.duration}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}
